import { useRef } from 'react';

export type ListView = 'loading' | 'empty' | 'content';

/**
 * Что рисовать на месте списка, чтобы при перезагрузке (фильтр, дата, язык) не мигало.
 *
 * Раньше было `list.length === 0 && !loading ? <Пусто/> : <Таблица/>`: список обнуляется перед запросом,
 * и на время загрузки на месте «Нет данных» вспыхивала таблица с одной шапкой и спиннером, а после
 * пустого ответа пропадала. Теперь, пока идёт запрос, держим то, чем закончилась прошлая загрузка:
 * было пусто — остаётся «Нет данных», была таблица — таблица (со своим спиннером в строке).
 * До первого ответа — `'loading'`: просто спиннер, без пустой шапки.
 *
 * @param isEmpty в списке нет строк
 * @param loading идёт загрузка первой страницы (не догрузка при скролле)
 */
export function useListView(isEmpty: boolean, loading: boolean): ListView {
  const started = useRef(false);
  const settled = useRef<ListView>('loading');

  if (loading) started.current = true;
  // Итог фиксируем только после настоящего запроса: до эффекта загрузки loading ещё false,
  // и пустой начальный список нельзя принять за «данных нет»
  else if (started.current) settled.current = isEmpty ? 'empty' : 'content';

  if (!isEmpty) return 'content';
  return loading ? settled.current : settled.current === 'loading' ? 'loading' : 'empty';
}
